import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useAppDispatch } from "src/store";
import store from "src/store"; // Import the Redux store instance
import { checkIsCompanyExists } from "src/store/company/companyApi";
import { showDevelopmentError, showToastNotification } from "src/utils";
import { getUserLatestTrackerConfig } from "src/store/trackers/trackersApi";
import {
  fetchUser,
  fetchCompany,
  fetchWhiteLabel,
  fetchAllDashboards,
  fetchRecentDashboards,
  fetchAllWidgetsInfo,
  fetchEventsByCompanyId,
  updateLatestTrackerConfig,
  fetchTrackersByCompanyId,
  fetchTrackersByIds,
  fetchRecentTrackers,
  fetchExternalCompanyById,
  fetchSubscriptionPlanById,
  fetchTrackersCollectionsByCompanyId,
} from "src/store/actions";
import {
  selectUser,
  selectUserId,
  selectCompany,
  selectCompanyId,
  selectCompanyById,
  selectEventsStatus,
  selectUserTrackers,
  selectUserCompanyId,
  selectTrackersStatus,
  selectDashboardsStatus,
  selectWhiteLabelStatus,
  selectWidgetsInfoStatus,
  selectCompanySubscriptionPlan,
  selectTrackersCollectionsStatus,
  selectTrackersCollectionById,
  selectTrackersEntities,
} from "src/store/selectors";
import * as dashboardsApi from "src/store/dashboards/dashboardsApi";
import { fetchAllDashboardsFromGlobalSearch } from "src/store/dashboards/dashboardsSlice";
import { setGlobalLoadMoreClicked } from "src/store/app/appSlice";

export const useUserDataLoader = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { id: userId } = useSelector(selectUser);

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");

  useEffect(() => {
    if (loadingStatus === "succeeded" && !userId)
      return setLoadingStatus("idle");

    if (loadingStatus !== "idle" || !userId) return;

    setLoadingStatus("loading");

    dispatch(fetchUser(userId))
      .unwrap()
      .then(() => setLoadingStatus("succeeded"))
      .catch((error) => {
        errorHandler(userId, "user", error);

        setLoadingStatus("failed");

        showToastNotification({ type: "error", text: t("user_data_error") });
      });
  }, [userId, loadingStatus, dispatch, t]);

  return {
    loadingStatus,
  };
};

export const useCompanyDataLoader = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { id: userId, companyId: userCompanyId } = useSelector(selectUser);

  const { id: companyId } = useSelector(selectCompany);

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");

  useEffect(() => {
    if (
      loadingStatus === "succeeded" &&
      (!companyId || userCompanyId !== companyId)
    )
      return setLoadingStatus("idle");

    if (loadingStatus !== "idle" || !userCompanyId) return;

    setLoadingStatus("loading");

    dispatch(fetchCompany(userCompanyId))
      .unwrap()
      .then(() => setLoadingStatus("succeeded"))
      .catch((error) => {
        errorHandler(userId, "company", error);

        setLoadingStatus("failed");

        showToastNotification({ type: "error", text: t("user_data_error") });
      });
  }, [t, userId, dispatch, companyId, loadingStatus, userCompanyId]);

  return { loadingStatus };
};

export const useExternalCompanyDataLoader = (companyId: Company.Data["id"]) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");

  const company = useSelector((state: Store.RootState) =>
    selectCompanyById(state, companyId),
  );

  useEffect(() => {
    if (!companyId || loadingStatus !== "idle" || company) return;

    (async () => {
      try {
        setLoadingStatus("loading");

        const isCompanyExists = await checkIsCompanyExists(companyId);

        if (isCompanyExists)
          await dispatch(fetchExternalCompanyById(companyId)).unwrap();

        setLoadingStatus("succeeded");
      } catch (error) {
        setLoadingStatus("failed");

        errorHandler(companyId, "company", error);

        showToastNotification({ type: "error", text: t("user_data_error") });
      }
    })();
  }, [companyId, loadingStatus, dispatch, t, company]);

  return { loadingStatus };
};

export const useWhiteLabelDataLoader = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useSelector(selectUserId);
  const loadingStatus = useSelector(selectWhiteLabelStatus);

  useEffect(() => {
    if (loadingStatus !== "idle") return;

    (async () => {
      try {
        await dispatch(fetchWhiteLabel()).unwrap();
      } catch (error) {
        errorHandler(userId, "company", error);
        showToastNotification({ type: "error", text: t("user_data_error") });
      }
    })();
  }, [userId, loadingStatus, dispatch, t]);

  return { loadingStatus };
};

export const useDashboardsLoader = () => {
  const dispatch = useAppDispatch();

  const loadingStatus = useSelector(selectDashboardsStatus);
  const companyId = useSelector(selectUserCompanyId);
  const user = useSelector(selectUser);
  const { hasLoadedAll, lastVisibleDoc } = useSelector(
    (state: Store.RootState) => state.dashboards,
  );

  const loadRecentDashboards = useCallback(async () => {
    if (!companyId) return;

    try {
      await dispatch(
        fetchRecentDashboards({ companyId, limit: 10, lastVisibleDoc }),
      ).unwrap();
    } catch (error) {
      const errorTitle = "ERROR IN RECENT DASHBOARDS FETCHING";
      showDevelopmentError({ additionalTexts: [errorTitle], error });
    }
  }, [companyId, dispatch, lastVisibleDoc]);

  const loadMoreDashboards = useCallback(async () => {
    if (!companyId || hasLoadedAll) return;

    try {
      await dispatch(
        fetchRecentDashboards({
          companyId,
          limit: 10,
          lastVisibleDoc,
          isLoadMore: true,
        }),
      ).unwrap();
    } catch (error) {
      const errorTitle = "ERROR IN MORE DASHBOARDS FETCHING";
      showDevelopmentError({ additionalTexts: [errorTitle], error });
    }
  }, [companyId, dispatch, hasLoadedAll, lastVisibleDoc]);

  const loadAllDashboards = useCallback(async () => {
    if (!companyId || hasLoadedAll) return;

    try {
      await dispatch(fetchAllDashboards(companyId)).unwrap();
    } catch (error) {
      const errorTitle = "ERROR IN ALL DASHBOARDS FETCHING";
      showDevelopmentError({ additionalTexts: [errorTitle], error });
    }
  }, [companyId, dispatch, hasLoadedAll]);

  const loadAllDashboardsForGlobalSearch = useCallback(async () => {
    if (!companyId || hasLoadedAll) return;

    try {
      await dispatch(
        fetchAllDashboardsFromGlobalSearch({ companyId }),
      ).unwrap();
    } catch (error) {
      const errorTitle = "ERROR IN ALL DASHBOARDS FETCHING (Global Search)";
      showDevelopmentError({ additionalTexts: [errorTitle], error });
    }
  }, [companyId, dispatch, hasLoadedAll]);

  const loadAllTrackersForGlobalSearch = useCallback(async () => {
    if (!companyId) return;

    try {
      await dispatch(fetchTrackersByCompanyId({ companyId })).unwrap();
    } catch (error) {
      const errorTitle = "ERROR IN ALL TRACKERS FETCHING (Global Search)";
      showDevelopmentError({ additionalTexts: [errorTitle], error });
    }
  }, [companyId, dispatch]);

  const loadDashboardsByIds = useCallback(
    async (dashboardIds: Dashboard.Data["id"][]) => {
      if (!dashboardIds.length) return;

      try {
        const dashboards = await dashboardsApi.getDashboardsByIds(dashboardIds);
        // Add each dashboard to the Redux store
        dashboards.forEach((dashboard: Dashboard.Data) => {
          dispatch({ type: "dashboards/addOne", payload: dashboard });
        });
      } catch (error) {
        const errorTitle = "ERROR IN DASHBOARDS BY IDS FETCHING";
        showDevelopmentError({ additionalTexts: [errorTitle], error });
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (loadingStatus !== "idle" || !companyId) return;

    // First load recently viewed dashboards from the user preferences
    if (user.lastViewedDashboardIds && user.lastViewedDashboardIds.length > 0) {
      const recentIds = user.lastViewedDashboardIds.slice(0, 10);
      loadDashboardsByIds(recentIds);
    }

    // Then load recent dashboards by updated date
    loadRecentDashboards();
  }, [
    loadingStatus,
    companyId,
    dispatch,
    user.lastViewedDashboardIds,
    loadRecentDashboards,
    loadDashboardsByIds,
  ]);

  return {
    loadingStatus,
    loadMoreDashboards,
    loadAllDashboards,
    hasLoadedAll,
    loadAllDashboardsForGlobalSearch,
    loadAllTrackersForGlobalSearch,
  };
};

export const useEventsLoader = () => {
  const dispatch = useAppDispatch();

  const loadingStatus = useSelector(selectEventsStatus);
  const companyId = useSelector(selectUserCompanyId);

  useEffect(() => {
    if (loadingStatus !== "idle" || !companyId) return;

    (async () => {
      try {
        await dispatch(fetchEventsByCompanyId(companyId)).unwrap();
      } catch (error) {
        const errorTitle = "ERROR IN ALL EVENTS FETCHING";

        showDevelopmentError({ additionalTexts: [errorTitle], error });
      }
    })();
  }, [loadingStatus, companyId, dispatch]);

  return { loadingStatus };
};

export const useSubscriptionPlanLoader = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { id: userId } = useSelector(selectUser);

  const { subscriptionPlanId: companySubscriptionPlanId } =
    useSelector(selectCompany);

  const subscriptionPlan = useSelector(selectCompanySubscriptionPlan);

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");

  useEffect(() => {
    if (loadingStatus === "succeeded" && !subscriptionPlan)
      return setLoadingStatus("idle");

    if (loadingStatus !== "idle" || !companySubscriptionPlanId) return;

    setLoadingStatus("loading");

    dispatch(fetchSubscriptionPlanById(companySubscriptionPlanId))
      .unwrap()
      .then(() => setLoadingStatus("succeeded"))
      .catch((error) => {
        errorHandler(userId, "subscriptionPlan", error);

        setLoadingStatus("failed");

        showToastNotification({ type: "error", text: t("user_data_error") });
      });
  }, [
    t,
    userId,
    dispatch,
    loadingStatus,
    subscriptionPlan,
    companySubscriptionPlanId,
  ]);

  return { loadingStatus };
};

export const useTrackersLoader = () => {
  const dispatch = useAppDispatch();

  const loadingStatus = useSelector(selectTrackersStatus);
  const companyId = useSelector(selectUserCompanyId);
  const user = useSelector(selectUser);
  const { hasLoadedAll, lastVisibleDoc } = useSelector(
    (state: Store.RootState) => state.trackers,
  );

  const loadRecentTrackers = useCallback(async () => {
    if (!companyId) return;

    try {
      await dispatch(
        fetchRecentTrackers({ companyId, limit: 10, lastVisibleDoc }),
      ).unwrap();
    } catch (error) {
      const errorTitle = "ERROR IN RECENT TRACKERS FETCHING";
      showDevelopmentError({ additionalTexts: [errorTitle], error });
    }
  }, [companyId, dispatch, lastVisibleDoc]);

  const loadMoreTrackers = useCallback(async () => {
    if (!companyId || hasLoadedAll) return;

    try {
      await dispatch(
        fetchRecentTrackers({ companyId, limit: 10, lastVisibleDoc }),
      ).unwrap();
    } catch (error) {
      const errorTitle = "ERROR IN MORE TRACKERS FETCHING";
      showDevelopmentError({ additionalTexts: [errorTitle], error });
    }
  }, [companyId, dispatch, hasLoadedAll, lastVisibleDoc]);

  const loadAllTrackers = useCallback(async () => {
    if (!companyId || hasLoadedAll) return;

    try {
      await dispatch(fetchTrackersByCompanyId({ companyId })).unwrap();
    } catch (error) {
      const errorTitle = "ERROR IN ALL TRACKERS FETCHING";
      showDevelopmentError({ additionalTexts: [errorTitle], error });
    }
  }, [companyId, dispatch, hasLoadedAll]);

  const loadAllTrackersForGlobalSearch = useCallback(async () => {
    if (!companyId) return;

    try {
      await dispatch(fetchTrackersByCompanyId({ companyId })).unwrap();
    } catch (error) {
      const errorTitle = "ERROR IN ALL TRACKERS FETCHING (Global Search)";
      showDevelopmentError({ additionalTexts: [errorTitle], error });
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    if (loadingStatus !== "idle" || !companyId) return;

    // Load recently viewed trackers first if available
    if (user.lastViewedTrackerIds && user.lastViewedTrackerIds.length > 0) {
      dispatch(fetchTrackersByIds(user.lastViewedTrackerIds.slice(0, 10)));
    }

    // Then load recent trackers by updated date
    loadRecentTrackers();
  }, [
    loadingStatus,
    companyId,
    dispatch,
    user.lastViewedTrackerIds,
    loadRecentTrackers,
  ]);

  return {
    loadingStatus,
    loadMoreTrackers,
    loadAllTrackers,
    hasLoadedAll,
    loadAllTrackersForGlobalSearch,
  };
};

export const useTrackersCollectionsLoader = () => {
  const dispatch = useAppDispatch();

  const companyId = useSelector(selectUserCompanyId);

  const loadingStatus = useSelector(selectTrackersCollectionsStatus);

  useEffect(() => {
    if (loadingStatus !== "idle" || !companyId) return;

    (async () => {
      try {
        await dispatch(fetchTrackersCollectionsByCompanyId(companyId)).unwrap();
      } catch (error) {
        const errorTitle = "ERROR IN COMPANY TRACKERS COLLECTIONS FETCHING";
        showDevelopmentError({ additionalTexts: [errorTitle], error });
      }
    })();
  }, [loadingStatus, companyId, dispatch]);

  return { loadingStatus };
};

export const useWidgetsDataLoader = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useSelector(selectUserId);
  const loadingStatus = useSelector(selectWidgetsInfoStatus);

  useEffect(() => {
    if (loadingStatus !== "idle") return;

    (async () => {
      try {
        await dispatch(fetchAllWidgetsInfo()).unwrap();
      } catch (error) {
        errorHandler(userId, "widgetsData", error);
        showToastNotification({ type: "error", text: t("user_data_error") });
      }
    })();
  }, [userId, loadingStatus, dispatch, t]);

  return { loadingStatus };
};

export const useUserLatestTrackerConfigLoader = (
  trackersLoadingStatus: LoadingStatus,
) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const userId = useSelector(selectUserId);

  const companyId = useSelector(selectCompanyId);

  const trackers = useSelector(selectUserTrackers);

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");

  useEffect(() => {
    if (trackersLoadingStatus !== "succeeded") return;

    if (loadingStatus === "succeeded" && !userId)
      return setLoadingStatus("idle");

    if (loadingStatus !== "idle") return;

    if (!trackers.length) {
      setLoadingStatus("succeeded");

      return;
    }

    setLoadingStatus("loading");

    getUserLatestTrackerConfig(trackers)
      .then((value) => {
        dispatch(updateLatestTrackerConfig(value));

        setLoadingStatus("succeeded");
      })
      .catch((error) => {
        errorHandler(userId, "latestTrackerConfig", error);

        setLoadingStatus("failed");

        showToastNotification({ type: "error", text: t("user_data_error") });
      });
  }, [
    t,
    userId,
    dispatch,
    trackers,
    companyId,
    loadingStatus,
    trackersLoadingStatus,
  ]);

  return { loadingStatus };
};

export const useDashboardTopicsLoader = (
  dashboardId?: Dashboard.Data["id"],
) => {
  const dispatch = useAppDispatch();
  const trackersCollection = useSelector((state: Store.RootState) =>
    selectTrackersCollectionById(state, dashboardId || ""),
  );
  const trackers = useSelector(selectTrackersEntities);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 5;
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Force a re-render to get updated trackers
  const [forceRefresh, setForceRefresh] = useState(0);

  // Cleanup any pending timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Reset loading state when dashboard changes
  useEffect(() => {
    if (dashboardId) {
      setLoadingStatus("idle");
      setRetryCount(0);
    }
  }, [dashboardId]);

  useEffect(() => {
    if (!dashboardId) return;

    // If we don't have trackers collection yet but still have retries remaining
    if (
      !trackersCollection &&
      loadingStatus === "idle" &&
      retryCount < maxRetries
    ) {
      // Set a timer to retry
      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount((count) => count + 1);
      }, 1000); // Retry after 1 second

      return;
    }

    // If retries exhausted and still no trackers collection, mark as failed
    if (
      !trackersCollection &&
      retryCount >= maxRetries &&
      loadingStatus === "idle"
    ) {
      setLoadingStatus("failed");
      return;
    }

    if (!trackersCollection || loadingStatus !== "idle") return;

    const trackerIds = trackersCollection.trackerIds || [];

    if (trackerIds.length === 0) {
      setLoadingStatus("succeeded");
      return;
    }

    // Check if trackers are already loaded
    const loadedTrackers = trackerIds.filter((id) => Boolean(trackers[id]));
    const allTrackersLoaded = loadedTrackers.length === trackerIds.length;

    if (allTrackersLoaded) {
      setLoadingStatus("succeeded");
      return;
    }

    // If we get here, we need to load or reload trackers
    setLoadingStatus("loading");

    // Always load all trackers to ensure we have the latest data
    dispatch(fetchTrackersByIds(trackerIds))
      .unwrap()
      .then(() => {
        // Force a re-render to get fresh trackers from Redux
        setForceRefresh((prev) => prev + 1);

        // Now check again with the latest trackers
        retryTimeoutRef.current = setTimeout(() => {
          // Get fresh trackers from Redux
          const currentTrackers = selectTrackersEntities(store.getState());
          const currentLoadedTrackers = trackerIds.filter((id) =>
            Boolean(currentTrackers[id]),
          );

          if (currentLoadedTrackers.length === 0) {
            if (retryCount < maxRetries) {
              // Reset to idle to trigger another load attempt
              setLoadingStatus("idle");
              setRetryCount((prev) => prev + 1);
            } else {
              setLoadingStatus("succeeded");
            }
            return;
          }

          setLoadingStatus("succeeded");
        }, 300); // Short delay to ensure Redux is updated
      })
      .catch((error) => {
        setLoadingStatus("failed");
        const errorTitle = "ERROR IN DASHBOARD TOPICS LOADING";
        showDevelopmentError({ additionalTexts: [errorTitle], error });
      });
  }, [
    dashboardId,
    trackersCollection,
    trackers,
    dispatch,
    loadingStatus,
    retryCount,
    forceRefresh,
  ]);

  return { loadingStatus };
};

export const useGlobalSearchLoader = () => {
  const dispatch = useAppDispatch();
  const companyId = useSelector(selectUserCompanyId);

  const hasLoadedAllDashboards = useSelector(
    (state: Store.RootState) => state.dashboards.hasLoadedAll,
  );

  const hasLoadedAllTrackers = useSelector(
    (state: Store.RootState) => state.trackers.hasLoadedAll,
  );

  const globalLoadMoreClicked = useSelector(
    (state: Store.RootState) => state.app.globalLoadMoreClicked,
  );

  const loadAllData = useCallback(async () => {
    if (!companyId) return;

    // Set the global state to indicate we've loaded all data
    dispatch(setGlobalLoadMoreClicked());

    try {
      // Load both dashboards and trackers in parallel
      await Promise.all([
        // Only load dashboards if not already loaded
        !hasLoadedAllDashboards
          ? dispatch(
              fetchAllDashboardsFromGlobalSearch({
                companyId,
                skipGlobalLoader: true,
              }),
            ).unwrap()
          : Promise.resolve([]),

        // Only load trackers if not already loaded
        !hasLoadedAllTrackers
          ? dispatch(
              fetchTrackersByCompanyId({ companyId, skipGlobalLoader: true }),
            ).unwrap()
          : Promise.resolve([]),
      ]);
    } catch (error) {
      const errorTitle = "ERROR LOADING ALL DATA FOR GLOBAL SEARCH";
      showDevelopmentError({ additionalTexts: [errorTitle], error });
    }
  }, [companyId, dispatch, hasLoadedAllDashboards, hasLoadedAllTrackers]);

  return {
    loadAllData,
    hasLoadedAll:
      globalLoadMoreClicked || (hasLoadedAllDashboards && hasLoadedAllTrackers),
    isFullyLoaded: hasLoadedAllDashboards && hasLoadedAllTrackers,
  };
};

function errorHandler(
  userId: string,
  type:
    | "user"
    | "company"
    | "whiteLabel"
    | "searches"
    | "subscriptionPlan"
    | "widgetsData"
    | "latestTrackerConfig",
  error: any,
) {
  let typeError = "";

  switch (type) {
    case "user":
      typeError = "Error occurred in readUser function";
      break;
    case "company":
      typeError = "Error occurred in readCompany function";
      break;
    case "whiteLabel":
      typeError = "Error occurred in readWhiteLabel function";
      break;
    case "searches":
      typeError = "Error occurred in readSearches function";
      break;
    case "subscriptionPlan":
      typeError = "Error occurred in readSubscriptionPlan function";
      break;
    case "widgetsData":
      typeError = "Error occurred in readWidgetsData function";
      break;
    case "latestTrackerConfig":
      typeError = "Error occurred in readLatestTrackerConfig function";
      break;
  }

  showDevelopmentError({
    error,
    additionalTexts: [typeError],
    reportMetadata: {
      message: `[${userId}]: Error on loading initial data for user: ${error.message}`,
    },
  });
}
